// src/pages/user/Vouchers.js
import React, { useState, useEffect, useRef } from 'react';
import { userAPI } from '../../services/api';
import Loading from '../../components/common/Loading';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FiCopy, FiCheck, FiDownload, FiPrinter, FiMapPin, FiCheckCircle } from 'react-icons/fi';

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const qrRefs = useRef({});

  useEffect(() => {
    loadVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await userAPI.getVouchers(filter);
      const payload = data?.data ?? data ?? [];
      setVouchers(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('Erreur chargement vouchers:', err);
      setError('Impossible de charger les vouchers. Réessayez plus tard.');
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', text: 'Actif' },
      used: { color: 'bg-gray-100 text-gray-800', text: 'Utilisé' },
      expired: { color: 'bg-red-100 text-red-800', text: 'Expiré' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status || 'Inconnu' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${config.color}`} aria-hidden>
        {config.text}
      </span>
    );
  };

  const copyCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); setCopiedCode(code); setTimeout(() => setCopiedCode(null), 2000); }
      catch (e) { /* ignore */ }
      textarea.remove();
    }
  };

  const formatDate = (d) => {
    try {
      if (!d) return '—';
      return format(new Date(d), 'dd MMMM yyyy', { locale: fr });
    } catch {
      return d;
    }
  };

  const escapeHtml = (unsafe = '') =>
    String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  // Récupère le SVG du QR déjà rendu dans la carte et le sérialise en chaîne.
  const getQrSvgString = (id) => {
    const container = qrRefs.current[id];
    const svg = container && container.querySelector('svg');
    if (!svg) return null;
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return new XMLSerializer().serializeToString(clone);
  };

  // SVG (chaîne) -> Image chargée (pour dessin sur canvas).
  const svgStringToImage = (svgString, size) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.width = size;
      img.height = size;
      img.onload = () => resolve(img);
      img.onerror = reject;
      // encodage unicode-safe
      const b64 = window.btoa(unescape(encodeURIComponent(svgString)));
      img.src = 'data:image/svg+xml;base64,' + b64;
    });

  // Texte multi-lignes sur canvas.
  const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
    const words = String(text || '').split(/\s+/);
    let line = '';
    let cursorY = y;
    for (let n = 0; n < words.length; n++) {
      const test = line ? line + ' ' + words[n] : words[n];
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, cursorY);
        line = words[n];
        cursorY += lineHeight;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, x, cursorY);
    return cursorY + lineHeight;
  };

  // TELECHARGER : génère une image PNG complète du voucher.
  const downloadVoucher = async (voucher, id) => {
    try {
      const W = 640;
      // Hauteur DYNAMIQUE : on part d'une base (bandeau + titre + QR + code + séparateur
      // + Marchand/Expire/Statut) et on ajoute la place des champs réellement présents,
      // pour éviter le grand vide blanc quand il y a peu d'infos, et le débordement quand
      // il y en a beaucoup.
      let H = 720;
      if (voucher.orderNumber) H += 30;
      if (voucher.purchasedAt) H += 30;
      if (voucher.amountPaid != null && voucher.amountPaid !== '') H += 30;
      if (voucher.durationMinutes) H += 30;
      if (voucher.merchantAddress || voucher.merchantPhone) {
        H += 70; // titre "Détails" + séparateur
        if (voucher.merchantAddress) H += 22 + Math.ceil(String(voucher.merchantAddress).length / 40) * 22;
        if (voucher.merchantPhone) H += 30;
      }
      const canvas = document.createElement('canvas');
      const scale = 2; // netteté (retina)
      canvas.width = W * scale;
      canvas.height = H * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      // fond
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      // cadre
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, W - 40, H - 40);
      // bandeau
      ctx.fillStyle = '#2563eb';
      ctx.fillRect(20, 20, W - 40, 70);
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 30px Arial, sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('MAXULA', 44, 56);
      ctx.font = '400 16px Arial, sans-serif';
      ctx.fillText('Voucher', W - 120, 56);

      ctx.textBaseline = 'alphabetic';
      let y = 130;

      // titre du deal
      ctx.fillStyle = '#0f172a';
      ctx.font = '700 22px Arial, sans-serif';
      const title = voucher.deal?.title || voucher.dealId?.title || 'Deal';
      y = wrapText(ctx, title, 44, y, W - 88, 30) + 6;

      // QR
      const svgStr = getQrSvgString(id);
      const qrSize = 280;
      const qrX = (W - qrSize) / 2;
      if (svgStr) {
        try {
          const qrImg = await svgStringToImage(svgStr, qrSize);
          ctx.drawImage(qrImg, qrX, y, qrSize, qrSize);
        } catch (e) {
          // si le QR échoue, on continue sans bloquer le téléchargement
          ctx.strokeStyle = '#e5e7eb';
          ctx.strokeRect(qrX, y, qrSize, qrSize);
        }
      }
      y += qrSize + 40;

      // code
      ctx.textAlign = 'center';
      ctx.fillStyle = '#6b7280';
      ctx.font = '400 15px Arial, sans-serif';
      ctx.fillText('Code du voucher', W / 2, y);
      y += 34;
      ctx.fillStyle = '#2563eb';
      ctx.font = "700 34px 'Courier New', monospace";
      ctx.fillText(voucher.code || '—', W / 2, y);
      y += 50;
      ctx.textAlign = 'left';

      // séparateur
      ctx.strokeStyle = '#e5e7eb';
      ctx.beginPath();
      ctx.moveTo(44, y);
      ctx.lineTo(W - 44, y);
      ctx.stroke();
      y += 34;

      // infos
      const line = (label, value) => {
        if (value == null || value === '') return;
        ctx.fillStyle = '#6b7280';
        ctx.font = '400 16px Arial, sans-serif';
        ctx.fillText(label, 44, y);
        ctx.fillStyle = '#0f172a';
        ctx.font = '700 16px Arial, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(String(value), W - 44, y);
        ctx.textAlign = 'left';
        y += 30;
      };
      // ===== Contrat (B2) =====
      if (voucher.orderNumber) line('N° commande', voucher.orderNumber);
      if (voucher.purchasedAt) line("Date d'achat", formatDate(voucher.purchasedAt));
      if (voucher.amountPaid != null && voucher.amountPaid !== '') {
        line('Montant payé', `${Number(voucher.amountPaid).toFixed(2)} ${voucher.currency || 'MAD'}`);
      }
      line('Marchand', voucher.merchantName || voucher.merchant?.businessName || voucher.merchantId?.businessName || '—');
      if (voucher.durationMinutes) line('Durée', `${voucher.durationMinutes} min`);
      line('Expire le', formatDate(voucher.expiresAt || voucher.expirationDate));
      line('Statut', getStatusText(voucher.status));

      // ===== Prestation (P1) : adresse + téléphone (sur plusieurs lignes) =====
      if (voucher.merchantAddress || voucher.merchantPhone) {
        y += 4;
        ctx.strokeStyle = '#e5e7eb';
        ctx.beginPath(); ctx.moveTo(44, y); ctx.lineTo(W - 44, y); ctx.stroke();
        y += 28;
        ctx.fillStyle = '#0f172a';
        ctx.font = '700 16px Arial, sans-serif';
        ctx.fillText('Détails de votre prestation', 44, y);
        y += 26;
        if (voucher.merchantAddress) {
          ctx.fillStyle = '#6b7280'; ctx.font = '400 15px Arial, sans-serif';
          ctx.fillText('Adresse', 44, y); y += 22;
          ctx.fillStyle = '#0f172a'; ctx.font = '400 15px Arial, sans-serif';
          y = wrapText(ctx, voucher.merchantAddress, 44, y, W - 88, 22) + 8;
        }
        if (voucher.merchantPhone) line('Téléphone', voucher.merchantPhone);
      }

      // footer
      ctx.fillStyle = '#9ca3af';
      ctx.font = '400 13px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Présentez ce QR code au partenaire à l\'adresse indiquée.', W / 2, y + 20);
      ctx.fillText('maxula.ma', W / 2, y + 42);
      ctx.textAlign = 'left';

      // download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `voucher-${voucher.code || 'maxula'}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    } catch (err) {
      console.error('Download voucher failed', err);
      alert('Échec du téléchargement du voucher. Utilisez le bouton Imprimer.');
    }
  };

  const getStatusText = (status) =>
    ({ active: 'Actif', used: 'Utilisé', expired: 'Expiré' }[status] || status || '—');

  // IMPRIMER : ouvre une fenêtre imprimable AVEC le QR inclus.
  const printVoucher = (voucher, id) => {
    const svgStr = getQrSvgString(id);
    const qrBlock = svgStr
      ? `<div class="qr">${svgStr}</div>`
      : '';
    const printable = `
      <html lang="fr">
        <head>
          <title>Voucher - ${escapeHtml(voucher.code || '')}</title>
          <meta charset="utf-8" />
          <style>
            @page { margin: 16mm; }
            body { font-family: 'Cairo', Arial, Helvetica, sans-serif; padding: 0; color: #0f172a; }
            .container { max-width: 640px; margin: 0 auto; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
            .head { background: #2563eb; color: #fff; padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; }
            .head .brand { font-size: 24px; font-weight: 800; letter-spacing: 1px; }
            .body { padding: 24px; text-align: center; }
            .title { font-size: 20px; font-weight: 700; margin: 0 0 16px; }
            .qr svg { width: 240px; height: 240px; }
            .label { color: #6b7280; font-size: 13px; margin-top: 16px; }
            .code { font-family: 'Courier New', monospace; font-size: 30px; font-weight: 700; color: #2563eb; margin: 6px 0 16px; }
            .meta { text-align: left; border-top: 1px solid #e5e7eb; padding-top: 16px; }
            .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 15px; }
            .row .k { color: #6b7280; } .row .v { font-weight: 700; }
            .footer { color: #9ca3af; font-size: 12px; text-align: center; padding: 12px; border-top: 1px solid #e5e7eb; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="head"><span class="brand">MAXULA</span><span>Voucher</span></div>
            <div class="body">
              <p class="title">${escapeHtml(voucher.deal?.title || voucher.dealId?.title || 'Deal')}</p>
              ${qrBlock}
              <div class="label">Code du voucher</div>
              <div class="code">${escapeHtml(voucher.code || '')}</div>
              <div class="meta">
                <div class="row"><span class="k">Statut</span><span class="v">${escapeHtml(getStatusText(voucher.status))} · Payé</span></div>
                ${voucher.orderNumber ? `<div class="row"><span class="k">N° commande</span><span class="v">${escapeHtml(String(voucher.orderNumber))}</span></div>` : ''}
                ${voucher.purchasedAt ? `<div class="row"><span class="k">Date d'achat</span><span class="v">${escapeHtml(formatDate(voucher.purchasedAt))}</span></div>` : ''}
                ${(voucher.amountPaid != null && voucher.amountPaid !== '') ? `<div class="row"><span class="k">Montant payé</span><span class="v">${Number(voucher.amountPaid).toFixed(2)} ${escapeHtml(voucher.currency || 'MAD')}</span></div>` : ''}
                <div class="row"><span class="k">Marchand</span><span class="v">${escapeHtml(voucher.merchantName || voucher.merchant?.businessName || voucher.merchantId?.businessName || '—')}</span></div>
                ${voucher.durationMinutes ? `<div class="row"><span class="k">Durée</span><span class="v">${escapeHtml(String(voucher.durationMinutes))} min</span></div>` : ''}
                <div class="row"><span class="k">Expire le</span><span class="v">${escapeHtml(formatDate(voucher.expiresAt || voucher.expirationDate))}</span></div>
              </div>
              ${(voucher.merchantAddress || voucher.merchantPhone) ? `
              <div class="meta" style="margin-top:12px;">
                <div style="font-weight:700;margin-bottom:6px;">Détails de votre prestation</div>
                ${voucher.merchantAddress ? `<div style="margin-bottom:6px;"><div class="k" style="color:#6b7280;">Adresse</div><div>${escapeHtml(voucher.merchantAddress)}</div></div>` : ''}
                ${voucher.merchantPhone ? `<div class="row"><span class="k">Téléphone</span><span class="v">${escapeHtml(voucher.merchantPhone)}</span></div>` : ''}
              </div>` : ''}
            </div>
            <div class="footer">Présentez ce QR code au partenaire à l'adresse indiquée — maxula.ma</div>
          </div>
        </body>
      </html>
    `;
    // PAS de noopener ici : sinon window.open renvoie null.
    const w = window.open('', '_blank');
    if (!w) {
      alert("Impossible d'ouvrir la fenêtre d'impression (popup bloquée). Autorisez les popups pour ce site.");
      return;
    }
    w.document.open();
    w.document.write(printable);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 500);
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Mes Vouchers</h2>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
          aria-label="Filtrer les vouchers"
        >
          <option value="active">Actifs</option>
          <option value="used">Utilisés</option>
          <option value="expired">Expirés</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded">{error}</div>
      )}

      {vouchers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600">Aucun voucher "{filter}"</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {vouchers.map((voucher) => {
            const id = voucher._id ?? voucher.id ?? voucher.code ?? Math.random().toString(36).slice(2, 9);
            return (
              <div key={id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold text-lg truncate" title={(voucher.deal?.title || voucher.dealId?.title) || ''}>
                    {(voucher.deal?.title || voucher.dealId?.title) || 'Deal'}
                  </h3>
                  {getStatusBadge(voucher.status)}
                </div>

                <div className="flex flex-col md:flex-row items-center md:items-stretch justify-center my-4 space-y-4 md:space-y-0 md:space-x-6">
                  <div className="flex-shrink-0" ref={(el) => { qrRefs.current[id] = el; }}>
                    <QRCodeSVG
                      value={voucher.code || ''}
                      size={160}
                      level="M"
                      role="img"
                      aria-label={`QR code pour le voucher ${voucher.code || ''}`}
                    />
                  </div>

                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-600 mb-1">Code du voucher</p>
                    <p className="text-2xl font-bold text-primary-600 font-mono">{voucher.code || '—'}</p>

                    <div className="mt-3 flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <button
                        type="button"
                        onClick={() => copyCode(voucher.code)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 text-sm"
                        aria-label={`Copier le code ${voucher.code}`}
                      >
                        {copiedCode === voucher.code ? <FiCheck /> : <FiCopy />}
                        {copiedCode === voucher.code ? 'Copié !' : 'Copier'}
                      </button>

                      <button
                        type="button"
                        onClick={() => downloadVoucher(voucher, id)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        aria-label={`Télécharger le voucher ${voucher.code}`}
                      >
                        <FiDownload />
                        Télécharger
                      </button>

                      <button
                        type="button"
                        onClick={() => printVoucher(voucher, id)}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm"
                        aria-label={`Imprimer le voucher ${voucher.code}`}
                      >
                        <FiPrinter />
                        Imprimer
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2 text-sm">
                  {/* ===== Lot B2 — Contrat : statut payé, référence, date, montant ===== */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 font-semibold text-xs px-2.5 py-0.5 rounded-full">
                      <FiCheckCircle size={13} />
                      Payé · Confirmé
                    </span>
                  </div>
                  {voucher.orderNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">N° commande:</span>
                      <span className="font-semibold">{voucher.orderNumber}</span>
                    </div>
                  )}
                  {(voucher.purchasedAt) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date d'achat:</span>
                      <span className="font-semibold">{formatDate(voucher.purchasedAt)}</span>
                    </div>
                  )}
                  {(voucher.amountPaid != null && voucher.amountPaid !== '') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant payé:</span>
                      <span className="font-semibold">{Number(voucher.amountPaid).toFixed(2)} {voucher.currency || 'MAD'}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marchand:</span>
                    <span className="font-semibold">{voucher.merchantName || (voucher.merchant?.businessName || voucher.merchantId?.businessName) || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expire le:</span>
                    <span className="font-semibold">{formatDate((voucher.expiresAt || voucher.expirationDate))}</span>
                  </div>
                  {voucher.usedAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Utilisé le:</span>
                      <span className="font-semibold">{format(new Date(voucher.usedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
                    </div>
                  )}
                </div>

                {/* ===== Lot P1 — Détails figés de la prestation =====
                    Ces informations ont été enregistrées AU MOMENT DE L'ACHAT : elles ne
                    changent pas si le partenaire déménage ensuite. Les anciens coupons
                    (émis avant cette version) n'ont pas de snapshot : on n'affiche alors
                    que ce qui existe, sans jamais planter. */}
                {(voucher.merchantAddress || voucher.merchantPhone || voucher.durationMinutes ||
                  voucher.instructions || (Array.isArray(voucher.conditions) && voucher.conditions.length > 0)) && (
                  <div className="border-t pt-4 mt-4 space-y-3 text-sm">
                    <p className="font-semibold text-gray-800">Détails de votre prestation</p>

                    {voucher.merchantAddress && (
                      <div>
                        <span className="text-gray-600 block mb-1">Adresse</span>
                        <span className="font-medium break-words block">{voucher.merchantAddress}</span>
                        <a
                          href={
                            (voucher.merchantLat != null && voucher.merchantLng != null)
                              ? `https://www.google.com/maps/search/?api=1&query=${voucher.merchantLat},${voucher.merchantLng}`
                              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(voucher.merchantAddress)}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium text-sm transition-colors"
                        >
                          <FiMapPin size={15} />
                          <span>Ouvrir l'itinéraire</span>
                        </a>
                      </div>
                    )}

                    {voucher.durationMinutes ? (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Durée:</span>
                        <span className="font-semibold">{voucher.durationMinutes} min</span>
                      </div>
                    ) : null}

                    {voucher.merchantPhone && (
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-gray-600">Téléphone:</span>
                        <a href={`tel:${voucher.merchantPhone}`} className="font-semibold text-primary-600 hover:underline">
                          {voucher.merchantPhone}
                        </a>
                      </div>
                    )}

                    {voucher.instructions && (
                      <div>
                        <span className="text-gray-600 block mb-1">Consignes</span>
                        <span className="text-gray-800 whitespace-pre-line break-words">{voucher.instructions}</span>
                      </div>
                    )}

                    {Array.isArray(voucher.conditions) && voucher.conditions.length > 0 && (
                      <div>
                        <span className="text-gray-600 block mb-1">Conditions</span>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-800">
                          {voucher.conditions.map((c, i) => (<li key={i} className="break-words">{String(c)}</li>))}
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 pt-1 border-t mt-2">
                      Présentez ce QR code au partenaire à l'adresse indiquée.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Vouchers;
